import isNil from "lodash/isNil";

import { isAdult, parseProfileDateOfBirth } from "@/utils/profile/age.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

// there is currently no situation that has "editButNotView" permissions
type Permission = "editAndView" | "none" | "view";

const isEditable = (permission: Permission): boolean => {
  return permission === "editAndView";
};

const isViewable = (permission: Permission): boolean => {
  return permission === "view" || permission === "editAndView";
};

type UserPermissions = Partial<Record<keyof FullProfileResponse, Permission>>;

const SupportPermissions: UserPermissions = {
  address: "editAndView",
  cellPhone: "editAndView",
  chosenName: "editAndView",
  contactPreferences: "editAndView",
  email: "view",
  firstName: "view",
  gender: "editAndView",
  languages: "editAndView",
  lastName: "view",
  preferences: "editAndView",
  pronouns: "editAndView",
  relationToPatient: "view",
  timezone: "editAndView",
};

const PatientAsProxyPermissions: UserPermissions = {
  address: "editAndView",
  cellPhone: "editAndView",
  chosenName: "editAndView",
  contactPreferences: "editAndView",
  dateOfBirth: "view",
  email: "view",
  firstName: "view",
  gender: "view",
  languages: "editAndView",
  lastName: "view",
  pronouns: "view",
  relationToPatient: "view",
  timezone: "editAndView",
};

const AdultPatientPermissions: UserPermissions = {
  address: "editAndView",
  cellPhone: "editAndView",
  chosenName: "editAndView",
  contactPreferences: "editAndView",
  dateOfBirth: "view",
  email: "view",
  firstName: "view",
  gender: "view",
  languages: "editAndView",
  lastName: "view",
  preferences: "editAndView",
  pronouns: "view",
  supportedBy: "view",
  timezone: "editAndView",
};

const MinorPatientPermissions: UserPermissions = {
  chosenName: "editAndView",
  dateOfBirth: "view",
  firstName: "view",
  gender: "view",
  lastName: "view",
  preferences: "editAndView",
  pronouns: "view",
  supportedBy: "view",
};

type UserPermissionsType =
  | "AdultPatient"
  | "MinorPatient"
  | "PatientAsProxy"
  | "Support";

const UserPermissionsTypes: Record<UserPermissionsType, UserPermissions> = {
  AdultPatient: AdultPatientPermissions,
  MinorPatient: MinorPatientPermissions,
  PatientAsProxy: PatientAsProxyPermissions,
  Support: SupportPermissions,
};

const pickUserPermissionsType = ({
  dateOfBirth,
  isPatient,
  isProxy,
}: {
  dateOfBirth?: string;
  isPatient?: boolean;
  isProxy?: boolean;
}): null | UserPermissionsType => {
  // if missing isPatient, return null, this information is required
  if (isNil(isPatient)) {
    return null;
  }

  if (!isPatient) {
    return "Support";
  }

  // if missing isProxy, that's fine
  if (isProxy) {
    return "PatientAsProxy";
  }

  // if missing date of birth, only return null if made it here
  if (isNil(dateOfBirth)) {
    return null;
  }

  if (isAdult(parseProfileDateOfBirth(dateOfBirth))) {
    return "AdultPatient";
  }

  return "MinorPatient";
};

type PermissionChecker = (field: keyof FullProfileResponse) => Permission;

// By default, any permissions not explicitly assigned default to "none" Permission
// ie. they are NOT viewable, they are NOT editable
const getPermissionChecker = (
  userPermissions: UserPermissions,
): PermissionChecker => {
  return (field: keyof FullProfileResponse) => {
    return userPermissions[field] ?? "none";
  };
};

const getPermissionsForUser = ({
  dateOfBirth,
  isPatient,
  isProxy,
}: {
  dateOfBirth?: string;
  isPatient?: boolean;
  isProxy?: boolean;
}): PermissionChecker => {
  const userPermissionsType = pickUserPermissionsType({
    dateOfBirth,
    isPatient,
    isProxy,
  });
  const userPermissions = isNil(userPermissionsType)
    ? {}
    : UserPermissionsTypes[userPermissionsType];
  return getPermissionChecker(userPermissions);
};

const hasRecordingConsentPermission = ({
  appUserDateOfBirth,
  isAppUserPatient,
  proxyPatients,
}: {
  appUserDateOfBirth?: string;
  isAppUserPatient: boolean;
  proxyPatients?: LinkedPatientProfile[];
}): boolean => {
  if (isAppUserPatient) {
    // if missing date of birth, return false
    if (isNil(appUserDateOfBirth)) {
      return false;
    }

    // only adult patients have recording consent permission, not minors
    return isAdult(parseProfileDateOfBirth(appUserDateOfBirth));
  }

  // if missing proxy patients, return false
  if (!isNotNilNotEmpty(proxyPatients)) {
    return false;
  }
  const areAnyProxyPatientsMinors = proxyPatients.some((patient) => {
    // if patient has no date of birth, treat as adult (no recording consent permission)
    if (isNil(patient.dateOfBirth)) {
      return false;
    }
    return !isAdult(parseProfileDateOfBirth(patient.dateOfBirth));
  });
  // a support user only has recording consent permission for minor patients
  return areAnyProxyPatientsMinors;
};

const proxyPatientsNeedingRecordingConsent = (
  proxyPatients: LinkedPatientProfile[] | undefined,
): LinkedPatientProfile[] => {
  if (!isNotNilNotEmpty(proxyPatients)) {
    return [];
  }
  // filter out proxy patients that have already given recording consent
  // or are not eligible for recording consent
  return proxyPatients.filter((patient) => {
    const { dateOfBirth, hasGivenRecordingConsent } = patient;

    if (isNil(dateOfBirth) || hasGivenRecordingConsent !== null) {
      return false;
    }
    const isAdultPatient = isAdult(parseProfileDateOfBirth(dateOfBirth));
    // if patient has not given recording consent, patient is a minor
    return !isAdultPatient;
  });
};

export {
  getPermissionChecker,
  getPermissionsForUser,
  hasRecordingConsentPermission,
  isEditable,
  isViewable,
  pickUserPermissionsType,
  proxyPatientsNeedingRecordingConsent,
  UserPermissionsTypes,
};
export type { Permission, PermissionChecker, UserPermissionsType };
